import { ChangeEvent } from "react";

import { BaseReactComponent } from "@/Common/Components/BaseReactComponent";
import { DropdownItem } from "@/Common/Models/UI/Inputs/DropdownItem";
import * as LocalOperations from "@/Common/Operations/operations.local";
import { LocalEvent } from "@/Common/Stores/Actions/LocalEvent";

interface IBaseSearchAutocompleteCustomProps<Model> {
    componentId: string;
    dataCy?: string;
    selectedItemList: Immutable.List<Model>;
    selectionOp?: number;
    clearOp?: number;

    onClearCallback?: (item?: DropdownItem<Model> | null) => void;
    disabled?: boolean;
    focusOnLoad?: boolean;

    valid?: boolean;
    useBlurEvent?: boolean;
    closeDropDownAfterSelection?: boolean;
    doNotEraseListAfterSelection?: boolean;
    canCloseDropdownAfterAllItemsSelected?: boolean;

    defaultTextValue?: string;
    value?: string;

    tabIndex?: number;
    allowMultipleItemSelection?: boolean;
    searchStrategy?: ISearchStrategy<Model>;

    dropDownListMaxHeight?: number;
    disableInputField?: boolean;

    interactiveOptions?: {
        element: React.ReactNode | React.ReactText,
        onClick: () => void, key: string, style: React.CSSProperties
    }[]

    changedTextValueOp?: number;

    onSearch?: (searchText: string) => Promise<DropdownItem<Model>[]>
    renderItem?: (model: Model, cssClass?: string) => React.ReactNode;
    onSelectionCallback?: (item: DropdownItem<Model>) => void;
    onTextChange?: (value: string) => void;
    alternateSelectedView?: (model: Model, onRemove: (e: any) => void) => JSX.Element;
    growSearchWidthToParentNode?: boolean;
    disableClearOnSelect?: boolean;
}

class BaseSearchAutocompleteCustomState<TModel> {
    public searchText: string;
    public expanded: boolean;
    public infocus: boolean;
    public itemList: DropdownItem<TModel>[];
    public receivedResultDate: Date;

    public constructor(s?: Partial<BaseSearchAutocompleteCustomState<TModel>>) {
        Object.assign(this, s);
    }

    public static default<TModel>(searchText: string = "", infocus: boolean = false): BaseSearchAutocompleteCustomState<TModel> {
        return new BaseSearchAutocompleteCustomState<TModel>({
            searchText,
            expanded: false,
            infocus,
            itemList: [],
            receivedResultDate: null
        });
    }
}

abstract class BaseSearchAutocompleteCustom<TModel> extends BaseReactComponent<IBaseSearchAutocompleteCustomProps<TModel>, BaseSearchAutocompleteCustomState<TModel>> {
    private debounceMs: number;
    private blurBuffer: number;
    private timeoutReference: any;
    private inputControlId: string;
    protected searchStrategy: ISearchStrategy<TModel>;
    protected abstract defaultSearchStrategy: ISearchStrategy<TModel>;

    private mistDsptchRference: string;
    private containerRef: HTMLDivElement;

    constructor(props: IBaseSearchAutocompleteCustomProps<TModel>) {
        super(props);
        this.debounceMs = 400;
        this.handleClickOutSide = this.handleClickOutSide.bind(this);
        this.inputControlId = "input_" + props.componentId;

        const searchText = props.defaultTextValue ?? props.value ?? "";
        this.state = BaseSearchAutocompleteCustomState.default<TModel>(searchText, props.focusOnLoad);
    }

    public componentDidUpdate(prevProps: IBaseSearchAutocompleteCustomProps<TModel>): void {
        const propsUnchanged =
        this.props?.value === prevProps.value &&
        this.props?.focusOnLoad === prevProps.focusOnLoad &&
        this.props?.searchStrategy === prevProps.searchStrategy &&
        this.props?.defaultTextValue === prevProps.defaultTextValue;

        if (propsUnchanged)
            return;

        const newState: Partial<BaseSearchAutocompleteCustomState<TModel>> = {};

        if (!IsNullOrUndefined(this.props.value)) {
            newState.searchText = this.props.value;
        }
        else if (this.props?.defaultTextValue !== prevProps.defaultTextValue) {
            newState.searchText = this.props.defaultTextValue ?? "";
        }

        if (this.props?.focusOnLoad !== prevProps?.focusOnLoad) {
            newState.infocus = this.props.focusOnLoad;
        }

        if (this.props?.searchStrategy) {
            this.searchStrategy = this.props.searchStrategy;
        }

        this.setState(state => new BaseSearchAutocompleteCustomState({
            ...state,
            expanded: false,
            itemList: [],
            ...newState
        }));
    }

    public componentDidMount(): void {
        if (this.props.useBlurEvent == null || this.props.useBlurEvent === false) {
            document.addEventListener("click", this.handleClickOutSide);
        }

        this.mistDsptchRference = MiscDispatcher.register(e => this.dispatcherEvents(e));
        this.searchStrategy = this.props.searchStrategy ?? this.defaultSearchStrategy;

        if (this.props.focusOnLoad)
            this.rawSetFocus();

        if (!IsNullOrWhiteSpace(this.props.defaultTextValue)) {
            window.setTimeout(
                () => this.executeSearch(this.props.defaultTextValue),
                200
            );
        }
    }

    private handleClickOutSide(event: MouseEvent): void {
        const scrollBarSelector = "div.ui-wrp-b";

        if (event.target instanceof Element) {
            if (event.target.closest(scrollBarSelector))
                event.preventDefault(); // ignore click on scrollbar

            else if (this.containerRef?.contains?.(event.target) === false)
                this.onClosed();
        }
    }

    public async executeSearch(text: string): Promise<void> {
        const response = this.props.onSearch
            ? await this.props.onSearch(text)
            : await this.searchStrategy.executeSearch(text);
        this.handleSearchResults(response);
    }

    public shouldComponentUpdate(nextProps: IBaseSearchAutocompleteCustomProps<TModel>, nextState: BaseSearchAutocompleteCustomState<TModel>): boolean {
        return (
            this.props.disabled !== nextProps.disabled
            || this.state.searchText !== nextState.searchText
            || this.props.selectedItemList !== nextProps.selectedItemList
            || this.state.itemList !== nextState.itemList
            || this.state.expanded !== nextState.expanded
            || this.props.defaultTextValue !== nextProps.defaultTextValue
            || this.props.valid !== nextProps.valid
            || this.props.value !== nextProps.value
        );
    }

    protected abstract renderLabel(model: TModel): string;
    protected abstract searchStrategyRenderItem(model: TModel, cssClass?: string): React.ReactNode;

    protected abstract getItemId(model: TModel): string | number;

    protected getListStyle(): React.CSSProperties {
        return {};
    }

    public render(): JSX.Element {
        const interactiveOptions = this.props.interactiveOptions ?? [];
        let expand = this.state.expanded && (this.state.itemList.length > 0 || this.props.interactiveOptions?.any());

        let css = this.props.valid === false ? "patient-autocomplete invalid" : "patient-autocomplete";
        if (this.props.disabled) {
            css += " disabled";
            expand = false;
        }

        const allowMultipleItemSelection = !!this.props.allowMultipleItemSelection;
        const listStyle = this.getListStyle();

        let style: React.CSSProperties = { position: "relative" };
        if (this.props.growSearchWidthToParentNode)
            style.flexGrow = 1;

        return (
            <div
                ref={ref => this.containerRef = ref}
                id={this.props.componentId}
                data-cy={this.props.dataCy}
                className={css}
                onClick={e => this.onContainerClick(e)}
                style={style}
            >
                <div className="flex-container agrb">
                    {this.props.selectedItemList?.map((item, idx) => {
                        const alternateView = this.props.alternateSelectedView?.(item, e => !this.props.disabled && this.removeItem(item, e));

                        return alternateView ?? (
                            <div key={idx} className="selected-item flex-container" onClick={e => this.stopPropagation(e)}>
                                <span className="content-text">{this.renderLabel(item)}</span>
                                <span
                                    className="remove-btn"
                                    onClick={e => {
                                        if (!this.props.disabled)
                                            this.removeItem(item, e);
                                    }}
                                />
                            </div>
                        );
                    })}
                    {!this.props.disableInputField && ((this.props.selectedItemList?.size ?? 0) === 0 || allowMultipleItemSelection) && (
                        <div className="patient-search-field">
                            <input
                                tabIndex={0}
                                id={this.inputControlId}
                                data-cy={this.props.dataCy ? `${this.props.dataCy}-input` : null}
                                onKeyDown={e => this.inputKeyDown(e)}
                                onBlur={() => this.onBlur()}
                                onChange={e => this.searchTextChanged(e)}
                                disabled={this.props.disabled}
                                value={this.state.searchText}
                                type="text"
                            />
                        </div>
                    )}
                </div>
                <div className="pr">
                    {expand && (
                        <div style={{ width: "100%", overflowX: "hidden", whiteSpace: "nowrap", maxHeight: this.props.dropDownListMaxHeight ?? "auto" }} className="ui-wrp-b">
                            <ul data-role="available-item" data-cy={this.props.dataCy ? `${this.props.dataCy}-list` : null}>
                                {interactiveOptions.any() && interactiveOptions.map(elem => (
                                    <li key={elem.key} style={elem.style} onClick={() => elem.onClick()}>
                                        <span>{elem.element}</span>
                                    </li>
                                ))}
                                {this.state.itemList.map((item, idx) => {
                                    const isSelected = this.props.selectedItemList?.some(d => this.getItemId(d) === this.getItemId(item.Item));
                                    const css = `${isSelected ? "selected" : ""} ${item.Active ? "pending" : ""}`.trim();

                                    return (
                                        <li
                                            id={"li_item_" + idx}
                                            className={css}
                                            key={idx}
                                            style={listStyle}
                                            onClick={e => this.onItemClick(item, e)}
                                        >
                                            {this.props.renderItem?.(item.Item, item?.CssClass)
                                                ?? this.searchStrategyRenderItem?.(item.Item, item?.CssClass)
                                            }
                                        </li>
                                    );
                                })}
                            </ul>
                        </div>
                    )}
                </div>
            </div>
        );
    }

    public componentWillUnmount(): void {
        clearTimeout(this.blurBuffer);
        if (this.props.useBlurEvent == null || this.props.useBlurEvent === false)
            document.removeEventListener("click", this.handleClickOutSide, false);

        MiscDispatcher.unregister(this.mistDsptchRference);
    }

    private dispatcherEvents(e: LocalEvent): void {
        if (e.Operation === LocalOperations.Misc.Focus && e.Get<string>("id") === this.props.componentId)
            window.setTimeout(() => this.showInputControl(), 200);
    }

    private showInputControl(): void {
        this.mutateState(
            s => {
                s.infocus = true;
                s.expanded = true;
            },
            () => this.rawSetFocus()
        );
    }

    private rawSetFocus(): void {
        $("#" + this.inputControlId).focus();
    }

    private onBlur(): void {
        if (this.props.useBlurEvent == null || this.props.useBlurEvent === true) {
            this.onControlLeave();
        }
    }

    private stopPropagation(e: React.MouseEvent): void {
        e.stopPropagation();
    }

    private async onContainerClick(e: React.MouseEvent): Promise<void> {
        e.stopPropagation();
        this.showInputControl();

        if (IsNullOrWhiteSpace(this.state.searchText)) {
            this.executeSearch("");
        }
    }

    private onControlLeave(): void {
        this.blurBuffer = window.setTimeout(() => this.onClosed(), 300);
    }

    private onClosed(): void {
        this.mutateState(s => {
            if (this.props.selectedItemList?.size > 0)
                s.infocus = false;
            s.expanded = false;
        });
    }

    private inputKeyDown(e: React.KeyboardEvent): void {
        if (e.keyCode === 8) {
            // delete last item
            if (e.ctrlKey) {
                if (this.state.searchText.length === 0) {
                    if (this.props.clearOp != null)
                        sessionStore.dispatch(this.props.clearOp);

                    this.props.onClearCallback?.();
                }
            }
            else {
                if (this.props.selectedItemList?.size > 0 && this.state.searchText.length === 0) {
                    const size = this.props.selectedItemList.size;

                    const model = new DropdownItem<TModel>({
                        Item: this.props.selectedItemList.get(size - 1),
                        Active: false
                    });

                    if (this.props.clearOp != null)
                        sessionStore.dispatch(this.props.clearOp, { value: model });

                    this.props.onClearCallback?.(model);
                }
            }
        }

        // up
        if (e.keyCode === 38) { e.preventDefault(); this.previousItem(); }

        // down
        if (e.keyCode === 40) { e.preventDefault(); this.nextItem(); }

        // enter
        if (e.keyCode === 13) { this.addSelectedItem(); }

        // escape
        if (e.keyCode === 27) {
            this.clearState();
        }
    }

    // keyboard triggered
    private nextItem(): void {
        const collection = this.state.itemList.toImmutable();
        const idx = this.indexOfSelectedItem();

        if (idx === -1)
            return;

        if (idx + 1 < collection.size) {
            collection.get(idx).Active = false;
            collection.get(idx + 1).Active = true;
            this.updateStateWithNewResults(collection.toArray(), true);
        }

        this.adjustScoll();
    }

    private adjustScoll(): void {
        const idx = this.indexOfSelectedItem();
        const container = $("#" + this.props.componentId);
        const scrollParent = container.find("div.ui-wrp-b");
        const ul = container.find('ul[data-role="available-item"]');
        const liSelected = ul.find("#li_item_" + idx);
        const firstItem = ul.find("#li_item_" + 0);

        if (scrollParent != null && liSelected != null && firstItem != null) {
            const scrollParentHeight = scrollParent.height();
            const currentScrollPosition = Math.abs(firstItem.position().top);

            const selectedItemRelativePosition = liSelected.position().top; // relative to CurrentScrollPosition
            const selectedItemHeight = liSelected.height();

            if (selectedItemRelativePosition < 0) // Item is above scroll position
                scrollParent.scrollTop(currentScrollPosition + selectedItemRelativePosition);
            else if (selectedItemRelativePosition + selectedItemHeight > scrollParentHeight) // Item is below scroll
                scrollParent.scrollTop(currentScrollPosition + (selectedItemRelativePosition + selectedItemHeight - scrollParentHeight)); // Add the offset to scroll position in order to fully include the last item
        }
    }

    // keyboard triggered
    private previousItem(): void {
        let idx = this.indexOfSelectedItem();
        if (idx === -1) {
            this.collapseDropdown();
            return;
        }

        const collection = this.state.itemList.toImmutable();
        if (idx === 0) {
            collection.get(0).Active = false;
            this.updateStateWithNewResults(collection.toArray(), false);
            return;
        }

        if (idx > 0) {
            collection.get(idx).Active = false;
            idx--;
            collection.get(idx).Active = true;
            this.updateStateWithNewResults(collection.toArray(), true);
        }

        else if (idx === 1) {
            collection.get(0).Active = true;
            collection.get(1).Active = false;
            this.updateStateWithNewResults(collection.toArray(), false);
        }

        this.adjustScoll();
    }

    // keyboard triggered
    private addSelectedItem(): void {
        const idx = this.indexOfSelectedItem();
        if (idx >= 0) {
            if (this.props.selectionOp != null)
                sessionStore.dispatch(this.props.selectionOp, { value: this.state.itemList[idx] });

            this.props.onSelectionCallback?.(this.state.itemList[idx]);

            if (this.props.canCloseDropdownAfterAllItemsSelected)
                this.closeDropdownAfterAllItemsSelected();

            if (!this.props.disableClearOnSelect) {
                this.clearQueryString(!!this.props.allowMultipleItemSelection);

                if (this.props.closeDropDownAfterSelection)
                    this.clearState(!this.props.doNotEraseListAfterSelection);
            }
        }
    }

    private updateStateWithNewResults(list: DropdownItem<TModel>[], expand: boolean): void {
        this.mutateState(s => {
            s.expanded = expand;
            s.itemList = list;
        });
    }

    // returns index of item in state.patients array of active item
    private indexOfSelectedItem(): number {
        if (this.state.itemList.length === 0)
            return -1;

        for (let i = 0; i < this.state.itemList.length; i++) {
            if (this.state.itemList[i].Active)
                return i;
        }

        return -1;
    }

    private onItemClick(item: DropdownItem<TModel>, e: React.MouseEvent): void {
        e.stopPropagation();
        if (this.blurBuffer != null) {
            clearTimeout(this.blurBuffer);
            this.blurBuffer = null;
        }

        if (this.props.selectionOp != null)
            sessionStore.dispatch(this.props.selectionOp, { value: item });

        this.props.onSelectionCallback?.(item);

        if (this.props.canCloseDropdownAfterAllItemsSelected)
            this.closeDropdownAfterAllItemsSelected();

        if (!this.props.disableClearOnSelect) {
            this.clearQueryString(!!this.props.allowMultipleItemSelection);

            if (this.props.closeDropDownAfterSelection)
                this.clearState();
        }
    }

    private async closeDropdownAfterAllItemsSelected(): Promise<void> {
        const itemsList = await this.props.searchStrategy.executeSearch(null);
        if (itemsList.length === this.props.selectedItemList.count())
            this.clearState();
    }

    private removeItem(item: TModel, e: React.MouseEvent): void {
        e.stopPropagation();
        const p = new DropdownItem<TModel>();
        p.Item = item;
        p.Active = false;

        this.clearQueryString();

        if (this.props.clearOp != null) {
            window.setTimeout(() => sessionStore.dispatch(this.props.clearOp, { value: p })); // TODO: this had `, 10` after the parentheses. Add the timeout correctly if it's needed, or remove this comment.
        }

        this.props.onClearCallback?.(p);
    }

    private searchTextChanged(e: ChangeEvent<HTMLInputElement>): void {
        if (this.timeoutReference != null) {
            clearTimeout(this.timeoutReference);
            this.timeoutReference = null;
        }

        const searchText = e.target.value;

        if (this.props.onTextChange) {
            this.props.onTextChange(searchText);
        }
        else {
            this.mutateState(s => s.searchText = searchText);
            if (this.props.changedTextValueOp)
                sessionStore.dispatch(this.props.changedTextValueOp, { value: searchText });
        }

        this.timeoutReference = window.setTimeout(
            async () => this.executeSearch(searchText),
            this.debounceMs
        );
    }

    private collapseDropdown(): void {
        this.changeDropdown(false);
    }

    private changeDropdown(expand: boolean): void {
        this.mutateState(s => s.expanded = expand);
    }

    private clearState(clearItemList: boolean = true): void {
        this.mutateState(
            s => {
                s.expanded = false;
                s.infocus = false;
                s.itemList = clearItemList ? [] : s.itemList;
                s.searchText = "";
            },
            () => this.props.onTextChange?.("")
        );
    }

    private clearQueryString(focusInputText: boolean = true): void {
        if (this.props.onTextChange) {
            this.props.onTextChange("");
            focusInputText && this.showInputControl();
        }
        else {
            this.mutateState(s => s.searchText = "", () => focusInputText && this.showInputControl());
        }
    }

    private handleSearchResults(searchResults: DropdownItem<TModel>[]): void {
        if (!searchResults)
            return;

        this.updateStateWithNewResults(searchResults, true);
    }
}

interface ISearchStrategy<TModel> {
    id: string;
    executeSearch(searchText: string): Promise<DropdownItem<TModel>[]>;
}

abstract class BaseSearchStrategy<TModel> implements ISearchStrategy<TModel> {
    public abstract id: string;
    public abstract executeSearch(searchText: string): Promise<DropdownItem<TModel>[]>;
}

export { BaseSearchAutocompleteCustom, BaseSearchStrategy };
